import { Data, Effect, Schema } from "effect";
import { DocVersion } from "./audit.ts";
import { CollabVersion } from "./collab.ts";
import {
  CurriculumPhase,
  CurriculumRole,
  CurriculumTags,
  PhaseTimestamps,
} from "./curriculum-tags.ts";
import { DocumentId } from "./ids.ts";
import { MODEL_EFFORTS, STUDIO_MODELS } from "./model-choice.ts";

// The boundary schemas for the Expert/UI document-lifecycle actions (clone + import, ADR-0024).
// These are **REST payloads**, not MCP tool args, so they live here rather than in `tool-args.ts`
// — the naming stays honest about who decodes them (the server REST edge and the web client).

// A generous-but-finite cap on imported markdown. The import body is held in memory, diffed from
// the empty string, and written to the log, so an unbounded payload is a memory-exhaustion risk.
// `ImportDocumentArgs.text` carries this cap so the `@effect/platform` body decoder refuses an
// oversized upload with a 422 *before* the string reaches `createDocument`.
export const MAX_IMPORT_BYTES = 1_048_576;

// The cap on an ad-hoc chat upload (ADR-0055), shared by the web client (rejects a larger drop
// before uploading) and the server (`createUpload` rejects one that slips past with a 413 — the
// authoritative per-route bound). It is NOT the pre-buffer transport ceiling: that is Bun's
// `maxRequestBodySize` (`BunHttpServer.layer`), and the `withMaxBodySize` middleware in
// `apps/server/src/http.ts` only bounds multipart bodies (none of the web routes parse multipart).
// Distinct from `MAX_IMPORT_BYTES`: an upload's bytes are forwarded to the Anthropic Files API —
// never diffed, logged, or held beyond the request — and ride the request body raw (no JSON
// envelope), so this is the exact on-the-wire size, not a pre-escaping figure. The Files API itself
// allows far more (500 MB). The bound is a memory guard: the body is buffered whole (as a
// `Uint8Array`, then re-wrapped as a Blob to POST upstream), so peak transient use is ~2× the file
// size per concurrent upload — 100 MiB is sized for the studio's low, human-driven upload
// concurrency (further bounded by a small client-side upload pool), not a streaming path. The upload
// is labeled with the file's real MIME (the browser `File.type`) and attached as the matching content
// block — an `image` block for `image/*`, a `document` block otherwise (text, markdown, PDFs); a file
// this large is far past the model's context window regardless. One source of truth for the two checks.
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

// Clone an existing curriculum: the source id decodes through the `DocumentId` brand, so a
// traversal/malformed id is rejected at the boundary.
export const CloneCurriculumArgs = Schema.Struct({
  sourceId: DocumentId,
});
export type CloneCurriculumArgs = typeof CloneCurriculumArgs.Type;

// Clone an existing curriculum from a CHOSEN historical version rather than its head (Clone from
// history): the source id decodes through the `DocumentId` brand, and `atVersion` through the
// branded `CollabVersion` so a negative / non-integer version is rejected at the boundary before it
// reaches `DocumentReadModel.at` (which would otherwise 404 it). The generalization of
// `CloneCurriculumArgs` — a plain Clone is `cloneAtVersion(sourceId, headVersion)`.
export const CloneAtVersionArgs = Schema.Struct({
  sourceId: DocumentId,
  atVersion: CollabVersion,
});
export type CloneAtVersionArgs = typeof CloneAtVersionArgs.Type;

// Create a new root curriculum — either branch of `POST /api/curricula/create` (ADR-0024,
// ADR-0105), replacing the former separate `/api/curricula/import` and `/api/unit-plan-launches`
// endpoints. Both branches mint a document immediately and return its id (`CreateDocumentResult`
// below): import lands real content synchronously; generate lands an empty placeholder
// (`origin: "generating"`) that the async Databricks job fills in later. `_tag` discriminates which
// controller path the server dispatches to.

// Import a new root curriculum from uploaded markdown text. The cap bounds the upload at the decode
// boundary (see `MAX_IMPORT_BYTES`). `tags` (the IDENTITY set: level + language) is REQUIRED (the
// curriculum-tags ADR): every imported curriculum declares its level and language up front, so the
// index and the agent's `list-curricula` can identify it without opening it. `status` is the
// lifecycle axis — optional here, defaulting to `latest` server-side (a fresh import is the current
// line); the Expert can change it afterwards from the curriculum page.
export const ImportCurriculumArgs = Schema.Struct({
  _tag: Schema.Literal("import"),
  text: Schema.String.pipe(Schema.maxLength(MAX_IMPORT_BYTES)),
  tags: CurriculumTags,
  // Both default server-side (canonical / structure) when omitted — see `importDocument`.
  role: Schema.optional(CurriculumRole),
  phase: Schema.optional(CurriculumPhase),
});
export type ImportCurriculumArgs = typeof ImportCurriculumArgs.Type;

// The l2_unit_plan pipeline's own closed parameter sets. `model` has no equivalent list in the
// pipeline, so `UNIT_PLAN_MODELS` mirrors the job's own default (`sonnet`); `UNIT_PLAN_EFFORTS`
// mirrors the pipeline's `VALID_EFFORTS`. Forwarded verbatim to the `claude` CLI, which does NOT
// validate them itself — an unknown value fails late, on the cluster, minutes into a run — hence the
// closed sets here rather than free strings. CON-3203 adds the studio models by full id (an Expert's
// pick from the generate form): `l2_unit_plan` passes an unaliased id straight through.
export const UNIT_PLAN_MODELS = ["sonnet", "opus", "haiku", ...STUDIO_MODELS] as const;
export const UNIT_PLAN_EFFORTS = MODEL_EFFORTS;
export const UNIT_PLAN_FINAL_STEPS = ["1", "3"] as const;

// Trigger an async curriculum-generation job (Databricks). Not derived from `CurriculumTags`: the
// pipeline takes raw `cefrLevel`/`l1`/`l2` strings, not the branded `CefrLevel`/`CurriculumLanguage`
// pair — the generator client (`UnitPlanGeneratorClient`) is the one that turns them into a
// `CurriculumTags` for the placeholder document it mints. `model`/`effort`/`finalStep`/`reteachOnly`
// are not surfaced by the web UI today but remain part of the wire shape for other callers.
export const GenerateCurriculumArgs = Schema.Struct({
  _tag: Schema.Literal("generate"),
  cefrLevel: Schema.String,
  l1: Schema.String,
  l2: Schema.String,
  model: Schema.optional(Schema.Literal(...UNIT_PLAN_MODELS)),
  effort: Schema.optional(Schema.Literal(...UNIT_PLAN_EFFORTS)),
  finalStep: Schema.optional(Schema.Literal(...UNIT_PLAN_FINAL_STEPS)),
  reteachOnly: Schema.optional(Schema.Boolean),
});
export type GenerateCurriculumArgs = typeof GenerateCurriculumArgs.Type;

export const CreateCurriculumArgs = Schema.Union(ImportCurriculumArgs, GenerateCurriculumArgs);
export type CreateCurriculumArgs = typeof CreateCurriculumArgs.Type;

// The success shape both branches return: the minted id of the new document.
export const CreateDocumentResult = Schema.Struct({
  documentId: DocumentId,
});
export type CreateDocumentResult = typeof CreateDocumentResult.Type;

// An explicit Expert import of empty source is a mistake to surface, not a silent no-op (unlike
// `seedDocument`, which skips empty bytes at boot). Shaped after `MarkupOnLoad`/`ForgedMarkup`.
export class EmptyDocumentSource extends Data.TaggedError("EmptyDocumentSource")<
  Record<never, never>
> {}

// The archive/restore/delete lifecycle actions all address one existing curriculum by id; the
// success echoes the id back (mirroring `CreateDocumentResult`).
export const CurriculumLifecycleArgs = Schema.Struct({
  documentId: DocumentId,
});
export type CurriculumLifecycleArgs = typeof CurriculumLifecycleArgs.Type;

export const CurriculumLifecycleResult = Schema.Struct({
  documentId: DocumentId,
});
export type CurriculumLifecycleResult = typeof CurriculumLifecycleResult.Type;

// The Expert/UI identity-edit action (the curriculum-tags ADR): update one curriculum's IDENTITY
// tags (level + language). The initial tags ride the genesis event; this appends one zero-byte
// `document.tags-updated` for every later change. Latest tags-bearing event wins — there is no
// per-field patch, so the payload always carries both tags (the UI form is pre-filled from the
// current set). Editable only from the curriculum page. The success echoes the tags as stored.
export const SetCurriculumTagsArgs = Schema.Struct({
  documentId: DocumentId,
  tags: CurriculumTags,
});
export type SetCurriculumTagsArgs = typeof SetCurriculumTagsArgs.Type;

export const SetCurriculumTagsResult = Schema.Struct({
  documentId: DocumentId,
  tags: CurriculumTags,
});
export type SetCurriculumTagsResult = typeof SetCurriculumTagsResult.Type;

// The Expert/UI status action (the identity/status split): move one curriculum along its lifecycle
// axis (`exploratory | latest | stable | archived`). One zero-byte `document.status-updated` event;
// the current status is the projection over the latest status-bearing event (`AuditLog
// .curriculumStatus`), folding the legacy `document.archived`/`unarchived` events too. Setting
// `archived` is how a curriculum leaves the working rotation (the former Archive action). Editable
// only from the curriculum page.
// The write side of the ROLE axis. `supersedes` is stamped when a promotion replaces an incumbent —
// the caller names the document being replaced so succession is recorded, not inferred.
export const SetCurriculumRoleArgs = Schema.Struct({
  documentId: DocumentId,
  role: CurriculumRole,
  supersedes: Schema.optional(DocumentId),
});
export type SetCurriculumRoleArgs = typeof SetCurriculumRoleArgs.Type;

export const SetCurriculumRoleResult = Schema.Struct({
  documentId: DocumentId,
  role: CurriculumRole,
});
export type SetCurriculumRoleResult = typeof SetCurriculumRoleResult.Type;

// The write side of the PHASE axis: forward-only, so a non-advancing request is refused rather than
// silently appended (`PhaseRegression`).
export const AdvanceCurriculumPhaseArgs = Schema.Struct({
  documentId: DocumentId,
  phase: CurriculumPhase,
});
export type AdvanceCurriculumPhaseArgs = typeof AdvanceCurriculumPhaseArgs.Type;

export const AdvanceCurriculumPhaseResult = Schema.Struct({
  documentId: DocumentId,
  phase: CurriculumPhase,
});
export type AdvanceCurriculumPhaseResult = typeof AdvanceCurriculumPhaseResult.Type;

// Publishing takes no version: the server pins the CURRENT head, so a client cannot pin something it
// merely believes to be current.
export const PublishCurriculumArgs = Schema.Struct({ documentId: DocumentId });
export type PublishCurriculumArgs = typeof PublishCurriculumArgs.Type;

export const PublishCurriculumResult = Schema.Struct({
  documentId: DocumentId,
  publishedVersion: DocVersion,
});
export type PublishCurriculumResult = typeof PublishCurriculumResult.Type;

// Opening / closing a rework on a live document. `phase` names the phase re-entered; closing takes
// none, since only one rework is open at a time.
export const OpenCurriculumReworkArgs = Schema.Struct({
  documentId: DocumentId,
  phase: CurriculumPhase,
});
export type OpenCurriculumReworkArgs = typeof OpenCurriculumReworkArgs.Type;

export const CurriculumReworkResult = Schema.Struct({
  documentId: DocumentId,
  reworkPhase: Schema.optional(CurriculumPhase),
});
export type CurriculumReworkResult = typeof CurriculumReworkResult.Type;

// The read side of one document's tags + status (`GET /api/curricula/tags?documentId=…`): the doc
// view fetches this to render the identity chips + status next to the title, and to pre-fill the
// edit dropdowns. `tags` absent = untagged (seeded or pre-tags legacy); `status` absent = no
// lifecycle status set yet. Neither is an error.
export const CurriculumTagsView = Schema.Struct({
  documentId: DocumentId,
  tags: Schema.optional(CurriculumTags),
  // The two axes that replaced `status`, plus the published pin and any open rework — what the
  // document-view header renders next to the title.
  role: CurriculumRole,
  phase: CurriculumPhase,
  publishedVersion: Schema.optional(DocVersion),
  reworkPhase: Schema.optional(CurriculumPhase),
  // When each phase was entered, and when the document first went live — the provenance the document
  // page shows beside created / last-edited.
  phaseEnteredAt: Schema.optional(PhaseTimestamps),
  publishedAt: Schema.optional(Schema.DateTimeUtc),
});
export type CurriculumTagsView = typeof CurriculumTagsView.Type;

// The read side of one document's immediate-parent lineage (`GET /api/curricula/lineage?documentId=`):
// the Metadata tab's Lineage section fetches this to render "cloned from <parentTitle> at version
// <clonedFromVersion>". It reads the document's OWN genesis (`document.created.v2[0]`) — the immediate
// parent only — never a multi-hop chain-walk. `clonedFrom`/`clonedFromVersion` absent
// = an origin document (imported/seeded), not an error; `parentTitle` is the parent's H1 resolved
// server-side (display-only — never leaks a raw id/`ViewerId` beyond the `DocumentId` already carried).
// A legacy head-clone (created before versioned clones) carries `clonedFrom` with `clonedFromVersion`
// absent — rendered gracefully as "cloned from <parentTitle>" with no version.
export const LineageView = Schema.Struct({
  documentId: DocumentId,
  clonedFrom: Schema.optional(DocumentId),
  clonedFromVersion: Schema.optional(CollabVersion),
  parentTitle: Schema.optional(Schema.String),
});
export type LineageView = typeof LineageView.Type;

// The curriculum-only lifecycle gate's failure (422): an id outside `curricula/` was passed to a
// curriculum-only action. `ensureCurriculum` (below) fails with this specifically (never the
// generalized `NotAllowedForRoot` below) so its existing REST and MCP call sites keep the exact
// same `kind: "NotACurriculum"` on the wire after U1's generalization.
export class NotACurriculum extends Data.TaggedError("NotACurriculum")<{
  readonly documentId: DocumentId;
  readonly root: string;
}> {}

// The generalized root gate's failure (U1): `ensureDocumentRoot(root)` (below) fails with this for
// any root, naming both the id and the root the caller required. Kept distinct from `NotACurriculum`
// because that tag reaches the wire (`kind: error._tag` in `rest.ts`) — reusing it for a
// non-curricula root gate (U4/U5/U7) would report `NotACurriculum` for a document that may well be a
// curriculum, just not eligible for the operation that gated it.
export class NotAllowedForRoot extends Data.TaggedError("NotAllowedForRoot")<{
  readonly documentId: DocumentId;
  readonly root: string;
}> {}

// The curriculum-only gate, generalized (U1) to a root-parameterized primitive: an operation for a
// given `DocumentId` root (`curricula/`, `reference-guides/`, ...) refuses any id outside it, naming
// both the id and the expected root on failure. `ensureCurriculum` (below) is the curriculum-only
// caller every existing lifecycle action still uses — the `resources/*.md` (rubric, glossary) are
// seed-synced documents the checker and the index must always see, so an id outside `curricula/` is a
// caller mistake here.
export const ensureDocumentRoot =
  (root: string) =>
  (documentId: DocumentId): Effect.Effect<void, NotAllowedForRoot> =>
    documentId.startsWith(root)
      ? Effect.void
      : Effect.fail(new NotAllowedForRoot({ documentId, root }));

// `ensureCurriculum` remaps `ensureDocumentRoot`'s generic failure back to `NotACurriculum` so
// existing call sites see byte-identical behavior after this generalization: REST `catchTags` it
// directly onto `kind: "NotACurriculum"`; MCP lowers it through `opErrorOf` to the same
// `OpError{kind: "NotACurriculum"}`.
export const ensureCurriculum = (documentId: DocumentId): Effect.Effect<void, NotACurriculum> =>
  ensureDocumentRoot("curricula/")(documentId).pipe(
    Effect.catchTag("NotAllowedForRoot", (error) =>
      Effect.fail(new NotACurriculum({ documentId: error.documentId, root: error.root })),
    ),
  );

// Permanent deletion is gated on the archived state: an active (working-list) curriculum must be
// archived first, so a stray delete click can never destroy a document still in use (409).
export class NotArchived extends Data.TaggedError("NotArchived")<{
  readonly documentId: DocumentId;
}> {}

// Phase is forward-only, so a request that would move a document backwards (or not move it at all) is
// refused rather than appended as a no-op (409). Carries both ends so the message can name them.
export class PhaseRegression extends Data.TaggedError("PhaseRegression")<{
  readonly documentId: DocumentId;
  readonly from: CurriculumPhase;
  readonly to: CurriculumPhase;
}> {}

// Publishing requires content that has cleared both reviews; anything earlier lacks the authored
// exercises downstream consumers need and would fail there instead (409).
export class NotPublishable extends Data.TaggedError("NotPublishable")<{
  readonly documentId: DocumentId;
  readonly phase: CurriculumPhase;
}> {}

// A rework re-enters a phase the document has already PASSED — asking for the current phase or one
// still ahead is a caller mistake, not a no-op, because there is nothing behind it to redo (409).
export class PhaseNotYetReached extends Data.TaggedError("PhaseNotYetReached")<{
  readonly documentId: DocumentId;
  readonly phase: CurriculumPhase;
  readonly currentPhase: CurriculumPhase;
}> {}

// Releasing, or advancing past a phase re-entered for rework, would ship work in progress: the
// rework closes first (409).
export class ReworkOpen extends Data.TaggedError("ReworkOpen")<{
  readonly documentId: DocumentId;
  readonly phase: CurriculumPhase;
}> {}

// Only one rework is open at a time (`document-create.ts`'s closing-takes-none invariant) — a second
// `openCurriculumRework` while one is already open would silently retarget it rather than layering a
// second phase behind the first, so it is refused instead (409).
export class ReworkAlreadyOpen extends Data.TaggedError("ReworkAlreadyOpen")<{
  readonly documentId: DocumentId;
  readonly phase: CurriculumPhase;
}> {}
